import type { MindItem } from '@kimuno/api-sdk/lib/types/responses/MindItem';
import { Box, Button, SvgIcon, Typography } from '@mui/material';
import React, { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { createCourseRegistration as createCourseRegistrationAction } from '../../../actions/course.actions';
import { CourseEnrolled } from '../../../ampli';
import { customSizes, spaces } from '../../../assets/styles/themes/tokens';
import Dialog from '../../../components/Dialog/Dialog';
import { COURSE_REGISTRATION_STATUS, routes } from '../../../constants';
import { selectUser } from '../../../reducers/user.selectors';
import services from '../../../services';
import { getAmpliContentData } from '../../../utils/ampli';
import { notifyBugsnagHandledError } from '../../../utils/bugsnagClient';
import getMuiIcon, { type IconName } from '../../../utils/getMuiIcon';
import {
	getPageSourceFromQuery,
	getPageSourceFromState,
	resolveDashboardSection,
} from '../../../utils/pageSource';
import useAction from '../../../utils/useAction';
import useRouter from '../../../utils/useRouter';
import useScreenSize from '../../../utils/useScreenSize';
import { generateCertificatePDF } from '../utils';
import { CourseCoverPageContext } from './CourseCoverPageProvider';

const CourseCoverButton: React.FC<{
	coverPageSection: 'summary' | 'certificate';
}> = ({ coverPageSection = 'summary' }) => {
	const { t } = useTranslation();
	const user = useSelector(selectUser);
	const { router, match } = useRouter();
	const { isXsScreen, isSmScreen } = useScreenSize();
	const { mindItemId, courseMindItem, accessLevel } = useContext(CourseCoverPageContext);
	const createCourseRegistration = useAction(createCourseRegistrationAction);
	const [isLoading, setIsLoading] = useState(false);
	const [isLimitDialogVisible, setLimitDialogVisible] = useState(false);
	const [limitDialogSettings, setLimitDialogSettings] = useState({
		title: '',
		content: '',
		primaryCta: {
			label: '',
			onClick: () => {},
		},
	});

	const handleWithinLimit = () => {
		setLimitDialogVisible(true);
		setLimitDialogSettings({
			title: t('Start a course?'),
			content: t('Your meinUnterricht Pro package allows you one training course per calendar month.'),
			primaryCta: {
				label: t('Start'),
				onClick: () => {
					handleCourseEnroll();
					setLimitDialogVisible(false);
				},
			},
		});
	};

	const redirectToCoursePlayer = () => {
		const pathname = `/${routes.coursePlayer.root.replace(':courseId', mindItemId)}`;
		// The cover page is a pass-through interstitial: forward the source it was
		// reached with (if any) to the player, which sends the Content Viewed event.
		// Course cards never link to the player directly (SEAR-683).
		const { query, state } = match.location;
		const pageSource = getPageSourceFromState(state) ?? getPageSourceFromQuery(query);
		const dashboardSection = resolveDashboardSection(pageSource, state, query);
		if (pageSource) {
			router.push({
				pathname,
				query: {
					page_source: pageSource,
					...(dashboardSection && { dashboard_section: dashboardSection }),
				},
				state: {
					pageSource,
					...(dashboardSection && { dashboardSection }),
				},
			});
		} else {
			router.push(pathname);
		}
	};

	const redirectToCertificatePage = async () => {
		setIsLoading(true);
		try {
			const certificateUrl = await generateCertificatePDF(user, mindItemId);
			window.open(certificateUrl, '_blank');
		} finally {
			setIsLoading(false);
		}
	};

	const trackCourseEnrolled = (mindItem: Partial<MindItem<'course'>>) => {
		const eventProperties = {
			content: getAmpliContentData(mindItem, 'mindItem'),
		};

		services.track.ampliEventTrack({
			event: new CourseEnrolled(eventProperties),
		});
	};

	const handleCourseEnroll = async () => {
		setIsLoading(true);
		try {
			await createCourseRegistration(mindItemId);
			trackCourseEnrolled(courseMindItem);
			redirectToCoursePlayer();
		} catch (error) {
			const errorMessage = `Failed to create course enrollment: ${(error as Error).message}; 
      MindItemId: ${mindItemId}
      Button type: ${coverPageSection}`;

			notifyBugsnagHandledError(new Error(errorMessage));
		} finally {
			setIsLoading(false);
		}
	};

	const getButtonProperties = (): {
		label: string;
		icon?: IconName;
		onClick?: () => void;
	} => {
		const defaultButtonState = {
			label: t('Start course'),
			icon: 'playOutlined' as IconName,
		};

		switch (courseMindItem.course?.registrationStatus) {
			case COURSE_REGISTRATION_STATUS.NONE:
				switch (accessLevel) {
					case 'full':
						return {
							label: t('Start course'),
							icon: 'playOutlined',
							onClick: handleCourseEnroll,
						};

					case 'within-limit':
						return {
							label: t('Start course'),
							icon: 'playOutlined',
							onClick: handleWithinLimit,
						};

					default:
						return defaultButtonState;
				}

			case COURSE_REGISTRATION_STATUS.PROGRESS:
				return accessLevel === 'none'
					? defaultButtonState
					: {
							label: t('Continue course'),
							icon: 'playOutlined',
							onClick: redirectToCoursePlayer,
						};

			case COURSE_REGISTRATION_STATUS.SUCCESS: {
				if (coverPageSection === 'summary') {
					return accessLevel === 'none'
						? defaultButtonState
						: {
								label: t('Go to course'),
								icon: 'playOutlined',
								onClick: redirectToCoursePlayer,
							};
				}
				return accessLevel === 'none'
					? {
							label: t('Start course'),
						}
					: {
							label: t('Go to certificate'),
							onClick: redirectToCertificatePage,
						};
			}

			default:
				return defaultButtonState;
		}
	};

	const shouldButtonBeDisabled = accessLevel === 'none' || accessLevel === null || isLoading;
	const buttonProps = getButtonProperties();
	return (
		<>
			{coverPageSection === 'summary' && (
				<Button
					sx={{ alignSelf: 'end', paddingX: spaces.lg, flexShrink: 0 }}
					startIcon={buttonProps.icon && <SvgIcon component={getMuiIcon(buttonProps.icon)} />}
					variant="contained"
					size="large"
					color="primary"
					fullWidth={isXsScreen || isSmScreen}
					onClick={buttonProps.onClick}
					disabled={shouldButtonBeDisabled}
					data-testid="start-course-button"
					type="button"
					aria-label={buttonProps.label}
					aria-busy={isLoading}
					aria-disabled={shouldButtonBeDisabled}
				>
					{buttonProps.label}
				</Button>
			)}
			{coverPageSection === 'certificate' && (
				<Button
					sx={{ alignSelf: 'start', paddingX: spaces.lg, flexShrink: 0 }}
					variant="outlined"
					color="secondary"
					size="large"
					onClick={buttonProps.onClick}
					disabled={shouldButtonBeDisabled}
					type="button"
					aria-label={buttonProps.label}
					aria-busy={isLoading}
					aria-disabled={shouldButtonBeDisabled}
				>
					{buttonProps.label}
				</Button>
			)}
			<Dialog
				open={isLimitDialogVisible}
				onClose={() => setLimitDialogVisible(false)}
				title={limitDialogSettings.title}
				htmlBody={
					<Box sx={{ paddingBottom: spaces.md }}>
						<Typography variant="body1">{limitDialogSettings.content}</Typography>
					</Box>
				}
				primaryCta={{ size: 'medium', ...limitDialogSettings.primaryCta }}
				secondaryCta={{
					variant: 'text',
					size: 'medium',
					label: t('Cancel'),
					onClick: () => setLimitDialogVisible(false),
				}}
				desktopWidth={customSizes.coursesLimitDialog.desktopWidth}
			/>
		</>
	);
};

export default CourseCoverButton;
